/* Diente — header: two-pane mega menu + mobile drill-down.

   Mega menu notes:
   - Click is the single source of truth. Hover only opens after a short
     intent delay; previously `mouseenter` opened the panel and the click that
     followed immediately closed it, so the button needed two clicks to work.
   - The panel height is capped to what is left of the viewport under the
     header, and each pane scrolls on its own, so nothing is ever unreachable.
   - Every rail category is a real destination. Hover or focus previews its
     pane, while click or Enter follows the configured category URL. */
(function () {
  'use strict';

  var OPEN_DELAY = 170;
  var CLOSE_DELAY = 180;

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ------------------------------------------------------------ mega menu */

  function initMega() {
    var root = document.querySelector('[data-mega-root]');
    if (!root) return;

    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-mega-toggle]'));
    var menus = [];
    var openTimer = null;
    var closeTimer = null;

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    }

    /* Each toggle owns the panel named by its aria-controls; at most one
       panel is open, so opening the second menu closes the first. */
    toggles.forEach(function (toggle) {
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel || !root.contains(panel)) return;
      menus.push({
        toggle: toggle,
        panel: panel,
        inner: panel.querySelector('.mega__inner'),
        tabs: Array.prototype.slice.call(panel.querySelectorAll('[data-mega-tab]')),
        panes: Array.prototype.slice.call(panel.querySelectorAll('[data-mega-pane]'))
      });
    });
    if (!menus.length) return;

    /* Leave the panel bounded by the space under the header, whatever the
       header's height happens to be (sticky, announcement bar, editor). */
    function measure(menu) {
      if (!menu.inner) return;
      var top = menu.panel.getBoundingClientRect().top;
      var strip = menu.panel.querySelector('.mega__strip');
      var stripHeight = strip ? strip.offsetHeight : 0;
      var available = window.innerHeight - top - stripHeight - 16;
      menu.inner.style.setProperty('--mega-max', Math.max(280, available) + 'px');
    }

    function select(menu, index, moveFocus) {
      menu.tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('data-active', active ? 'true' : 'false');
        if (active && moveFocus) tab.focus();
      });
      menu.panes.forEach(function (pane, i) {
        pane.hidden = i !== index;
        if (i === index) pane.scrollTop = 0;
      });
    }

    function openMenu() {
      for (var i = 0; i < menus.length; i++) {
        if (!menus[i].panel.hidden) return menus[i];
      }
      return null;
    }

    function close() {
      clearTimers();
      var current = openMenu();
      if (!current) return;
      current.panel.hidden = true;
      current.toggle.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-mega-open');
    }

    function open(menu) {
      clearTimers();
      var current = openMenu();
      if (current === menu) return;
      if (current) close();
      menu.panel.hidden = false;
      menu.toggle.setAttribute('aria-expanded', 'true');
      root.classList.add('is-mega-open');
      measure(menu);
    }

    menus.forEach(function (menu) {
      select(menu, 0, false);

      menu.toggle.addEventListener('click', function () {
        if (openMenu() === menu) close();
        else open(menu);
      });

      /* Hover opens only if the pointer actually rests on the button — passing
         over it on the way to the search field no longer throws the panel open. */
      menu.toggle.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        if (openMenu() === menu) return;
        openTimer = setTimeout(function () {
          open(menu);
        }, OPEN_DELAY);
      });

      menu.toggle.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
      });

      menu.tabs.forEach(function (tab, index) {
        tab.addEventListener('mouseenter', function () {
          select(menu, index, false);
        });
        tab.addEventListener('focus', function () {
          select(menu, index, false);
        });
        tab.addEventListener('keydown', function (event) {
          var tabs = menu.tabs;
          var next = null;
          if (event.key === 'ArrowDown') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          select(menu, next, true);
        });
      });
    });

    root.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
    });

    root.addEventListener('mouseleave', function () {
      clearTimeout(openTimer);
      closeTimer = setTimeout(close, CLOSE_DELAY);
    });

    document.addEventListener('keydown', function (event) {
      var current = openMenu();
      if (event.key === 'Escape' && current) {
        close();
        current.toggle.focus();
      }
    });

    document.addEventListener('click', function (event) {
      if (openMenu() && !root.contains(event.target)) close();
    });

    /* Close when focus leaves the header entirely — tabbing past the last
       link used to leave the panel hanging open over the page. */
    document.addEventListener('focusin', function (event) {
      if (openMenu() && !root.contains(event.target)) close();
    });

    function remeasure() {
      var current = openMenu();
      if (current) measure(current);
    }

    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, { passive: true });
  }

  /* --------------------------------------------------- mobile drill-down */

  function initDrill() {
    var drill = document.querySelector('[data-drill]');
    if (!drill) return;

    var rootPanel = drill.querySelector('[data-drill-root]');
    var scroller = drill.closest('.drawer__body') || drill;
    if (!rootPanel) return;

    function showRoot(focusIndex) {
      drill.querySelectorAll('[data-drill-sub]').forEach(function (sub) {
        sub.hidden = true;
      });
      rootPanel.hidden = false;
      scroller.scrollTop = 0;
      if (focusIndex != null) {
        var opener = drill.querySelector('[data-drill-open="' + focusIndex + '"]');
        if (opener) opener.focus();
      }
    }

    function showSub(index) {
      var sub = drill.querySelector('[data-drill-sub="' + index + '"]');
      if (!sub) return;
      rootPanel.hidden = true;
      drill.querySelectorAll('[data-drill-sub]').forEach(function (other) {
        other.hidden = other !== sub;
      });
      scroller.scrollTop = 0;
      var back = sub.querySelector('[data-drill-back]');
      if (back && !reduceMotion.matches) back.focus();
      else if (back) back.focus();
    }

    drill.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-drill-open]');
      if (opener) {
        event.preventDefault();
        showSub(opener.getAttribute('data-drill-open'));
        return;
      }
      var back = event.target.closest('[data-drill-back]');
      if (back) {
        event.preventDefault();
        showRoot(back.getAttribute('data-drill-back'));
      }
    });

    /* Reopening the drawer should always start at the category list. */
    var overlay = drill.closest('.overlay');
    if (overlay) {
      overlay.addEventListener('overlay:closed', function () {
        showRoot(null);
      });
    }
  }

  /* Keep the full utility header at the top of the page, then reclaim vertical
     space on small screens without removing search or the primary actions.
     Keyboard focus always expands the header before a control is used. */
  function initMobileCompactHeader() {
    var header = document.querySelector('.header--sticky');
    if (!header || header.dataset.mobileCompactInitialized === 'true') return;

    header.dataset.mobileCompactInitialized = 'true';
    var mobileViewport = window.matchMedia('(max-width: 900px)');
    var updateFrame = null;

    function update() {
      updateFrame = null;
      var shouldCompact = mobileViewport.matches
        && window.scrollY > 80
        && !header.contains(document.activeElement);
      header.classList.toggle('is-mobile-compact', shouldCompact);
    }

    function requestUpdate() {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(update);
    }

    function expandForFocus() {
      header.classList.remove('is-mobile-compact');
    }

    header.addEventListener('focusin', expandForFocus);
    header.addEventListener('focusout', requestUpdate);
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();
  }

  function init() {
    initMega();
    initDrill();
    initMobileCompactHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-mega-root], [data-drill]')) init();
  });
})();
